import { formatDateForContract, ruInflect } from "@/shared/utils";
import type { ContractDetailResponse } from "@/features/hr/contracts/types";
import {
  buildSalaryAmountText,
  buildTrialPeriodText,
  buildWorkScheduleContext,
  isWorkScheduleKey,
  renderWorkScheduleText,
  stripWorkScheduleBreakPhrase,
} from "@/features/hr/contracts/utils";

export interface ReplacePlaceholdersOptions {
  userData?: {
    organization?: {
      address_ru?: string;
      address_kk?: string;
    };
  };
  t?: (key: string, options?: Record<string, unknown>) => string;
}

function calculateEndDate(startDate: string): Date {
  const start = new Date(startDate);
  const end = new Date(start);
  end.setFullYear(end.getFullYear() + 1);
  return end;
}

export function replacePlaceholders(
  content: string,
  contractData: ContractDetailResponse,
  lang: "ru" | "kk",
  options?: ReplacePlaceholdersOptions,
): string {
  if (!content) return content;

  const scheduleContext = buildWorkScheduleContext(contractData);
  let result = scheduleContext?.break_start_time ? content : stripWorkScheduleBreakPhrase(content);

  const placeholderRegex = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;

  const startDateKk = contractData.start_date
    ? formatDateForContract(new Date(contractData.start_date), "kk")
    : "_____________";
  const startDateRu = contractData.start_date
    ? formatDateForContract(new Date(contractData.start_date), "ru")
    : "_____________";

  const endDate = contractData.start_date ? calculateEndDate(contractData.start_date) : null;
  const endDateKk = endDate ? formatDateForContract(endDate, "kk") : "_____________";
  const endDateRu = endDate ? formatDateForContract(endDate, "ru") : "_____________";

  const workCityRu = contractData.work_city ? contractData.work_city.name_ru : "_____________";
  const workCityKk = contractData.work_city ? contractData.work_city.name_kk : "_____________";

  const workScheduleTextRu = renderWorkScheduleText(contractData, "ru");
  const workScheduleTextKk = renderWorkScheduleText(contractData, "kk");

  const { salaryAmountDigits, salaryAmountTextKk, salaryAmountTextRu } = buildSalaryAmountText(contractData.salary_amount);

  const userData = options?.userData;

  result = result.replace(/\{\{\s*start_date_kk\s*\}\}/gi, startDateKk);
  result = result.replace(/\{\{\s*start_date_ru\s*\}\}/gi, startDateRu);

  result = result.replace(placeholderRegex, (_match, placeholder) => {
    const cleanPlaceholder = placeholder.trim();

    switch (cleanPlaceholder) {
      case "start_date_ru":
        return startDateRu;

      case "start_date_kk":
        return startDateKk;

      case "job_position_kk":
        return contractData.job_position_kk || "_____________";

      case "job_position_ru":
        return contractData.job_position_ru || "_____________";

      case "job_position_gent_ru":
        return contractData.job_position_ru ? ruInflect(contractData.job_position_ru, 2) : "_____________";

      case "job_description_list_ru":
        if (contractData.job_duties_ru && contractData.job_duties_ru.length > 0) {
          return contractData.job_duties_ru
            .map((duty, idx) => {
              if (idx === 0) return `- ${duty}`;
              if (idx < (contractData.job_duties_ru?.length ?? 0) - 1) return `\n- ${duty};`;
              return `\n- ${duty}`;
            })
            .join("");
        }
        return "_____________";

      case "job_description_list_kk":
        if (contractData.job_duties_kk && contractData.job_duties_kk.length > 0) {
          return contractData.job_duties_kk
            .map((duty, idx) => {
              if (idx === 0) return `- ${duty}`;
              if (idx < (contractData.job_duties_kk?.length ?? 0) - 1) return `\n- ${duty};`;
              return `\n- ${duty}`;
            })
            .join("");
        }
        return "_____________";

      case "trial_period_ru":
        return buildTrialPeriodText(contractData.trial_period, contractData.trial_duration_months).trialPeriodRu;

      case "trial_period_kk":
        return buildTrialPeriodText(contractData.trial_period, contractData.trial_duration_months).trialPeriodKk;

      case "work_schedule_ru":
        return workScheduleTextRu;

      case "work_schedule_kk":
        return workScheduleTextKk;

      case "salary_amount_ru":
      case "salary_amount_text_ru":
        return salaryAmountTextRu || "_____________";

      case "salary_amount_kk":
      case "salary_amount_text_kk":
        return salaryAmountTextKk || "_____________";

      case "salary_amount":
        return salaryAmountDigits;

      case "work_city":
        return lang === "ru" ? workCityRu : workCityKk;

      case "work_city_ru":
        return contractData.work_city ? contractData.work_city.name_ru : "_____________";

      case "work_city_kk":
        return contractData.work_city ? contractData.work_city.name_kk : "_____________";

      case "end_date_ru":
        return endDateRu;

      case "end_date_kk":
        return endDateKk;

      case "work_place_kk":
      case "work_address_kk":
        return contractData.work_format === "remote"
          ? contractData.employee_address_kk || "_____________"
          : userData?.organization?.address_kk || "_____________";

      case "work_place_ru":
      case "work_address_ru":
        return contractData.work_format === "remote"
          ? contractData.employee_address_ru || "_____________"
          : userData?.organization?.address_ru || "_____________";

      default: {
        if (isWorkScheduleKey(cleanPlaceholder)) {
          return scheduleContext?.[cleanPlaceholder as keyof typeof scheduleContext] ?? "_____________";
        }
        const contractDataRecord = contractData as unknown as Record<string, unknown>;
        const value = contractDataRecord[cleanPlaceholder];
        if (value !== undefined && value !== null) {
          if (typeof value === "object") {
            return "_____________";
          }
          return String(value);
        }
        return "_____________";
      }
    }
  });

  return result;
}
